import { useState, useEffect, useMemo } from 'react';
import { apiFetch } from '../api';

const TABS = [
  { key: 'not_reviewed', label: 'Not Reviewed' },
  { key: 'reviewed', label: 'Reviewed' },
  { key: 'archived', label: 'Archived' },
];

const RECEIPT_TABS = [
  { key: 'not_reviewed', label: 'Not Reviewed' },
  { key: 'reviewed', label: 'Reviewed' },
];

function ReceiptsPanel({ refreshKey, onReceiptCreated, onImportComplete, subjectFilter, panelTitle }) {
  const [emails, setEmails] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState([]);
  const [fetchBodies, setFetchBodies] = useState(false);
  const [getAll, setGetAll] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [activeTab, setActiveTab] = useState('not_reviewed');
  const [previewEmail, setPreviewEmail] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [emailBody, setEmailBody] = useState(null);
  const [emailBodyEmail, setEmailBodyEmail] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [savingPdf, setSavingPdf] = useState(false);
  const [visibleCount, setVisibleCount] = useState(100);
  const [creatingReceipt, setCreatingReceipt] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchData();
  }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchData() {
    try {
      if (subjectFilter) {
        // Receipt importer needs emails to display them
        const [emailsRes, accountsRes] = await Promise.all([
          apiFetch('/api/emails'),
          apiFetch('/api/emails/accounts'),
        ]);
        if (emailsRes.ok) setEmails(await emailsRes.json());
        if (accountsRes.ok) setAccounts(await accountsRes.json());
      } else {
        // Import-only panel just needs accounts
        const accountsRes = await apiFetch('/api/emails/accounts');
        if (accountsRes.ok) setAccounts(await accountsRes.json());
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function authorizeAccount(account) {
    try {
      const res = await apiFetch(`/api/emails/authorize?account=${encodeURIComponent(account)}`);
      if (res.ok) {
        const data = await res.json();
        window.open(data.url, '_blank', 'width=600,height=700');
      }
    } catch (err) {
      console.error(err);
    }
  }

  async function runImport(oneOnly = false) {
    setImporting(true);
    setProgress(['Starting import...']);
    setImportResult(null);

    try {
      const params = new URLSearchParams();
      if (fetchBodies) params.set('fetchBodies', 'true');
      if (getAll) params.set('getAll', 'true');
      if (oneOnly) params.set('limit', '1');
      const res = await apiFetch(`/api/emails/import?${params}`, { method: 'POST' });
      const data = await res.json().catch(() => null);
      if (res.ok && data) {
        setProgress(data.progress || []);
        setImportResult(data.result);
      } else {
        const errorDetail = data?.error || data?.message || `HTTP ${res.status}: ${res.statusText}`;
        setProgress((p) => [...p, `Import failed: ${errorDetail}`]);
        if (data?.progress) setProgress(data.progress);
        if (data?.result) setImportResult(data.result);
      }
    } catch (err) {
      setProgress((p) => [...p, `Error: ${err.message}`]);
    } finally {
      setImporting(false);
      await fetchData();
      if (onImportComplete) onImportComplete();
    }
  }

  async function peekNext() {
    setImporting(true);
    setProgress(['Peeking at next new email...']);
    setImportResult(null);
    try {
      const res = await apiFetch('/api/emails/peek-next');
      const data = await res.json();
      if (data.found) {
        setProgress([`Found: ${data.email.subject}`, `From: ${data.email.from}`, `Date: ${data.email.date}`]);
        // Show in the email body viewer (it will fetch body on-demand from Gmail)
        viewEmailBody(data.email);
      } else {
        setProgress(['No new emails found.']);
      }
    } catch (err) {
      setProgress([`Error: ${err.message}`]);
    } finally {
      setImporting(false);
    }
  }

  async function deauthorize(account) {
    try {
      await apiFetch(`/api/emails/deauthorize?account=${encodeURIComponent(account)}`, { method: 'DELETE' });
      await fetchData();
    } catch (err) {
      console.error(err);
    }
  }

  async function updateStatus(messageId, status) {
    setEmails((prev) =>
      prev.map((r) => (r.messageId === messageId ? { ...r, status } : r))
    );
    try {
      await apiFetch('/api/emails/status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messageId, status }),
      });
    } catch (err) {
      console.error('Failed to update status:', err);
      await fetchData();
    }
  }

  async function createReceiptFromEmail(email) {
    setCreatingReceipt(email.messageId);
    try {
      // Extract vendor name from "From" field
      const fromMatch = email.from.match(/^"?([^"<]+)"?\s*</);
      const vendor = fromMatch ? fromMatch[1].trim() : email.from;

      // Parse date
      let receiptDate = '';
      try {
        const d = new Date(email.date);
        if (!isNaN(d)) receiptDate = d.toISOString().split('T')[0];
      } catch {}

      // Create the receipt record
      const res = await apiFetch('/api/receipt-records', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source: 'email',
          sourceAccount: email.account,
          sourceEmailId: email.messageId,
          vendor,
          date: receiptDate,
          description: email.subject,
        }),
      });

      if (res.ok) {
        const receipt = await res.json();

        // Copy email attachments to the receipt
        if (email.hasAttachments && email.attachmentNames?.length > 0) {
          for (const filename of email.attachmentNames) {
            try {
              const attachRes = await apiFetch(
                `/api/emails/attachment?account=${encodeURIComponent(email.account)}&messageId=${encodeURIComponent(email.messageId)}&filename=${encodeURIComponent(filename)}`
              );
              if (attachRes.ok) {
                const blob = await attachRes.blob();
                const formData = new FormData();
                formData.append('file', blob, filename);
                await apiFetch(`/api/receipt-records/${receipt.id}/attachment`, {
                  method: 'POST',
                  body: formData,
                });
              }
            } catch {}
          }
        }

        // Mark email as reviewed
        await updateStatus(email.messageId, 'reviewed');
        // Notify parent to refresh Receipts panel
        if (onReceiptCreated) onReceiptCreated();
      }
    } catch (err) {
      console.error('Failed to create receipt:', err);
    } finally {
      setCreatingReceipt(null);
    }
  }

  async function loadPreview(email, filename) {
    setPreviewEmail(email);
    setPreviewFile(filename);
    setPreviewLoading(true);
    setPreviewUrl(null);

    try {
      const url = `/api/emails/attachment?account=${encodeURIComponent(email.account)}&messageId=${encodeURIComponent(email.messageId)}&filename=${encodeURIComponent(filename)}`;
      const res = await apiFetch(url);
      if (res.ok) {
        const blob = await res.blob();
        const objectUrl = URL.createObjectURL(blob);
        setPreviewUrl(objectUrl);
      }
    } catch (err) {
      console.error('Failed to load preview:', err);
    } finally {
      setPreviewLoading(false);
    }
  }

  function closePreview() {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewEmail(null);
    setPreviewFile(null);
    setPreviewUrl(null);
  }

  function switchPreviewFile(filename) {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    loadPreview(previewEmail, filename);
  }

  async function viewEmailBody(email) {
    setEmailBodyEmail(email);
    setEmailLoading(true);
    setEmailBody(null);
    try {
      const url = `/api/emails/email-body?account=${encodeURIComponent(email.account)}&messageId=${encodeURIComponent(email.messageId)}`;
      const res = await apiFetch(url);
      if (res.ok) {
        const data = await res.json();
        setEmailBody(data);
      }
    } catch (err) {
      console.error('Failed to load email body:', err);
    } finally {
      setEmailLoading(false);
    }
  }

  function closeEmailBody() {
    setEmailBodyEmail(null);
    setEmailBody(null);
    fetchData();
  }

  async function saveAsPdf() {
    if (!emailBodyEmail || !emailBody) return;
    setSavingPdf(true);

    try {
      let contentHtml = emailBody.html;
      if (!contentHtml && emailBody.text) {
        contentHtml = `<pre style="white-space:pre-wrap;font-family:sans-serif;font-size:14px;">${emailBody.text.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>`;
      }
      if (!contentHtml) return;

      // Extract just the body content if it's a full HTML document
      const bodyMatch = contentHtml.match(/<body[^>]*>([\s\S]*)<\/body>/i);
      const bodyContent = bodyMatch ? bodyMatch[1] : contentHtml;

      // Extract style tags from the original HTML
      const styleMatches = contentHtml.match(/<style[^>]*>[\s\S]*?<\/style>/gi);
      const styles = styleMatches ? styleMatches.join('\n') : '';

      // Create a visible container behind the modal overlay
      const container = document.createElement('div');
      container.style.position = 'fixed';
      container.style.top = '0';
      container.style.left = '0';
      container.style.width = '800px';
      container.style.zIndex = '-1';
      container.style.background = '#fff';
      container.style.color = '#000';
      container.style.padding = '20px';
      container.style.overflow = 'visible';
      container.innerHTML = styles + bodyContent;
      document.body.appendChild(container);

      // Wait for images to load
      const images = container.querySelectorAll('img');
      if (images.length > 0) {
        await Promise.allSettled(
          Array.from(images).map(
            (img) =>
              new Promise((resolve) => {
                if (img.complete) return resolve();
                img.onload = resolve;
                img.onerror = resolve;
              })
          )
        );
      }
      // Small delay to ensure rendering is complete
      await new Promise((r) => setTimeout(r, 500));

      const { default: html2pdf } = await import('html2pdf.js');
      const pdfBlob = await html2pdf()
        .set({
          margin: [10, 10, 10, 10],
          filename: `email-${emailBodyEmail.messageId}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            logging: false,
            windowWidth: 800,
          },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
        })
        .from(container)
        .outputPdf('blob');

      document.body.removeChild(container);

      // Upload to backend
      const formData = new FormData();
      formData.append('file', pdfBlob, `email-${emailBodyEmail.messageId}.pdf`);

      await apiFetch(
        `/api/emails/save-pdf?account=${encodeURIComponent(emailBodyEmail.account)}&messageId=${encodeURIComponent(emailBodyEmail.messageId)}`,
        { method: 'POST', body: formData }
      );
    } catch (err) {
      console.error('Failed to save PDF:', err);
    } finally {
      setSavingPdf(false);
    }
  }

  function isImage(filename) {
    return /\.(png|jpg|jpeg|gif|webp)$/i.test(filename);
  }

  function isPdf(filename) {
    return /\.pdf$/i.test(filename);
  }

  const configuredAccounts = ['timmhanson@gmail.com', 'tim@timhanson.net'];
  const unauthorizedAccounts = configuredAccounts.filter((a) => !accounts.includes(a));

  const baseEmails = useMemo(() => {
    if (!subjectFilter) return emails;
    const prefix = subjectFilter.toLowerCase();
    return emails.filter((r) => r.subject && r.subject.toLowerCase().startsWith(prefix));
  }, [emails, subjectFilter]);

  const filtered = useMemo(() => {
    let list = baseEmails.filter((r) => (r.status || 'not_reviewed') === activeTab);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((r) =>
        (r.subject && r.subject.toLowerCase().includes(q)) ||
        (r.from && r.from.toLowerCase().includes(q)) ||
        (r.attachmentNames && r.attachmentNames.some((n) => n.toLowerCase().includes(q)))
      );
    }
    return list.sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [baseEmails, activeTab, search]);

  const tabCounts = useMemo(() => {
    const counts = {};
    (subjectFilter ? RECEIPT_TABS : TABS).forEach((t) => {
      counts[t.key] = baseEmails.filter((r) => (r.status || 'not_reviewed') === t.key).length;
    });
    return counts;
  }, [baseEmails, subjectFilter]);

  if (loading) {
    return (
      <section className="card">
        <h2>{panelTitle || 'Email Importer'}</h2>
        <p className="loading">Loading...</p>
      </section>
    );
  }

  return (
    <section className="card receipts-section">
      <div className="goals-header">
        <h2 style={{ margin: 0 }}>{panelTitle || 'Email Importer'}{subjectFilter && <span style={{ fontSize: 13, fontWeight: 400, color: '#71717a' }}> ({baseEmails.length} emails)</span>}</h2>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#a1a1aa', cursor: 'pointer' }}>
            <input type="checkbox" checked={fetchBodies} onChange={(e) => setFetchBodies(e.target.checked)} />
            Fetch Bodies
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#a1a1aa', cursor: 'pointer' }}>
            <input type="checkbox" checked={getAll} onChange={(e) => setGetAll(e.target.checked)} />
            Get All
          </label>
          <button className="manage-btn" onClick={() => runImport(false)} disabled={importing}>
            {importing ? 'Importing...' : 'Import'}
          </button>
          <button className="manage-btn" onClick={() => runImport(true)} disabled={importing}
            style={{ background: '#2a2b35' }}>
            Retrieve One
          </button>
          <button className="manage-btn" onClick={peekNext} disabled={importing}
            style={{ background: '#2a2b35' }}>
            Peek Next
          </button>
        </div>
      </div>

      {unauthorizedAccounts.length > 0 && (
        <div className="receipts-auth">
          <span className="receipts-auth-label">Authorize:</span>
          {unauthorizedAccounts.map((account) => (
            <button
              key={account}
              className="receipts-auth-btn"
              onClick={() => authorizeAccount(account)}
            >
              {account}
            </button>
          ))}
        </div>
      )}
      {accounts.length > 0 && (
        <div className="receipts-authorized">
          {accounts.map((account) => (
            <span key={account} className="receipts-account-tag">
              {account}
              <button className="receipts-reauth" onClick={() => deauthorize(account)} title="Re-authorize">&times;</button>
            </span>
          ))}
        </div>
      )}

      {(importing || progress.length > 0) && (
        <div className="receipts-progress" style={{ maxHeight: 200, overflowY: 'auto', fontSize: 13, padding: '10px 12px' }}>
          {progress.map((msg, i) => (
            <div key={i} className="progress-line">{msg}</div>
          ))}
          {importing && <div className="progress-line progress-active">Working...</div>}
        </div>
      )}

      {importResult && !importing && (
        <div className="receipts-result" style={{ flexWrap: 'wrap', gap: '8px 16px', padding: '10px 12px' }}>
          <div className="result-stat">
            <span className="result-num" style={importResult.imported > 0 ? { color: '#22c55e' } : {}}>{importResult.imported}</span> new emails
          </div>
          <div className="result-stat">
            <span className="result-num">{importResult.cached}</span> existing
          </div>
          <div className="result-stat">
            <span className="result-num">{importResult.attachmentsDownloaded}</span> attachments
          </div>
          <div className="result-stat">
            <span className="result-num">{importResult.totalCached}</span> total
          </div>
          {importResult.bodiesBackfilled > 0 && (
            <div className="result-stat">
              <span className="result-num" style={{ color: '#22c55e' }}>{importResult.bodiesBackfilled}</span> bodies fetched
            </div>
          )}
          {importResult.missingBodies > 0 && (
            <div className="result-stat">
              <span className="result-num" style={{ color: '#f87171' }}>{importResult.missingBodies}</span> missing body
            </div>
          )}
          {importResult.missingBodies === 0 && importResult.bodiesBackfilled === 0 && (
            <div className="result-stat">
              <span className="result-num" style={{ color: '#22c55e' }}>0</span> missing body
            </div>
          )}
          {importResult.errors?.length > 0 && (
            <div className="result-errors">
              {importResult.errors.map((e, i) => (
                <div key={i} className="result-error">{e}</div>
              ))}
            </div>
          )}
        </div>
      )}

      {!subjectFilter && (
        <div style={{ color: '#71717a', fontSize: 13, padding: '8px 0' }}>
          Use the <strong>Email Browser</strong> panel to view and search emails.
        </div>
      )}

      {subjectFilter && (<>
      <div className="receipts-tabs">
        {RECEIPT_TABS.map((tab) => (
          <button
            key={tab.key}
            className={`receipts-tab${activeTab === tab.key ? ' active' : ''}`}
            onClick={() => { setActiveTab(tab.key); setVisibleCount(100); }}
          >
            {tab.label} ({tabCounts[tab.key] || 0})
          </button>
        ))}
      </div>

      <input
        type="text"
        value={search}
        onChange={(e) => { setSearch(e.target.value); setVisibleCount(100); }}
        placeholder="Search emails by subject, sender, or attachment..."
        className="sfin-tx-search"
        style={{ marginBottom: 8 }}
      />

      <div className="receipts-list">
        {filtered.length === 0 && (
          <div className="receipts-empty">No emails in this category</div>
        )}
        {filtered.slice(0, visibleCount).map((r) => (
          <div key={r.messageId} className="receipt-item">
            <div className="receipt-info">
              <span className="receipt-subject">{r.subject}</span>
              <span className="receipt-from">{r.from}</span>
            </div>
            <div className="receipt-meta">
              <span className="receipt-date">
                {new Date(r.date).toLocaleDateString('en-US', {
                  timeZone: 'America/Chicago',
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </span>
              {r.hasAttachments && (
                <span className="receipt-attachment-badge">
                  {r.attachmentNames?.length || 0} file{(r.attachmentNames?.length || 0) !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            <div className="receipt-actions">
              <button
                className="receipt-action-btn view-btn"
                onClick={() => viewEmailBody(r)}
              >
                View
              </button>
              {r.hasAttachments && r.attachmentNames?.length > 0 && (
                <button
                  className="receipt-action-btn view-btn"
                  onClick={() => loadPreview(r, r.attachmentNames[0])}
                >
                  View Attachments
                </button>
              )}
              {subjectFilter && (
                <button
                  className="receipt-action-btn import-btn"
                  onClick={() => createReceiptFromEmail(r)}
                  disabled={creatingReceipt === r.messageId}
                >
                  {creatingReceipt === r.messageId ? 'Creating...' : 'Create Receipt'}
                </button>
              )}
              {!subjectFilter && activeTab !== 'not_reviewed' && (
                <button
                  className="receipt-action-btn review-btn"
                  onClick={() => updateStatus(r.messageId, 'not_reviewed')}
                >
                  Not Reviewed
                </button>
              )}
              {!subjectFilter && activeTab !== 'reviewed' && (
                <button
                  className="receipt-action-btn import-btn"
                  onClick={() => updateStatus(r.messageId, 'reviewed')}
                >
                  Reviewed
                </button>
              )}
              {!subjectFilter && activeTab !== 'archived' && (
                <button
                  className="receipt-action-btn ignore-btn"
                  onClick={() => updateStatus(r.messageId, 'archived')}
                >
                  Archive
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      {visibleCount < filtered.length && (
        <button
          className="receipts-load-more"
          onClick={() => setVisibleCount((c) => c + 100)}
        >
          Load More ({filtered.length - visibleCount} remaining)
        </button>
      )}
      </>)}

      {previewEmail && (
        <div className="modal-overlay" onClick={closePreview}>
          <div className="modal-content receipt-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="receipt-preview-header">
              <h2>{previewFile}</h2>
              {previewEmail.attachmentNames.length > 1 && (
                <div className="receipt-preview-file-selector">
                  {previewEmail.attachmentNames.map((fn) => (
                    <button
                      key={fn}
                      className={`receipt-file-tab${fn === previewFile ? ' active' : ''}`}
                      onClick={() => switchPreviewFile(fn)}
                    >
                      {fn}
                    </button>
                  ))}
                </div>
              )}
              <button className="modal-close" onClick={closePreview}>&times;</button>
            </div>
            <div className="receipt-preview-body">
              {previewLoading && <p className="loading">Loading preview...</p>}
              {!previewLoading && previewUrl && isPdf(previewFile) && (
                <iframe
                  src={previewUrl}
                  title={previewFile}
                  className="receipt-preview-iframe"
                />
              )}
              {!previewLoading && previewUrl && isImage(previewFile) && (
                <img
                  src={previewUrl}
                  alt={previewFile}
                  className="receipt-preview-image"
                />
              )}
              {!previewLoading && previewUrl && !isPdf(previewFile) && !isImage(previewFile) && (
                <div className="receipt-preview-unsupported">
                  <p>Preview not available for this file type.</p>
                  <a
                    href={previewUrl}
                    download={previewFile}
                    className="receipt-download-link"
                  >
                    Download {previewFile}
                  </a>
                </div>
              )}
              {!previewLoading && !previewUrl && (
                <p className="empty">Could not load attachment</p>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Email Body Modal */}
      {emailBodyEmail && (
        <div className="modal-overlay" onClick={closeEmailBody}>
          <div className="modal-content receipt-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="receipt-preview-header">
              <h2>{emailBodyEmail.subject}</h2>
              <button
                className="receipt-action-btn import-btn"
                onClick={saveAsPdf}
                disabled={savingPdf || emailLoading || !emailBody}
                style={{ flexShrink: 0 }}
              >
                {savingPdf ? 'Saving...' : 'Save as PDF'}
              </button>
              <button className="modal-close" onClick={closeEmailBody}>&times;</button>
            </div>
            <div className="receipt-email-meta">
              <span>From: {emailBodyEmail.from}</span>
              <span>Date: {new Date(emailBodyEmail.date).toLocaleDateString('en-US', {
                timeZone: 'America/Chicago',
                month: 'short', day: 'numeric', year: 'numeric',
                hour: 'numeric', minute: '2-digit',
              })}</span>
            </div>
            <div className="receipt-preview-body">
              {emailLoading && <p className="loading">Loading email...</p>}
              {!emailLoading && emailBody?.html && (
                <iframe
                  srcDoc={emailBody.html}
                  title="Email body"
                  className="receipt-email-iframe"
                  sandbox="allow-same-origin"
                />
              )}
              {!emailLoading && !emailBody?.html && emailBody?.text && (
                <pre className="receipt-email-text">{emailBody.text}</pre>
              )}
              {!emailLoading && !emailBody && (
                <p className="empty">Could not load email body. Account may need re-authorization.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default ReceiptsPanel;
